package com.speedsoftware.nibbler.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.IssueType
import com.speedsoftware.nibbler.models.SprintStatus
import com.speedsoftware.nibbler.models.TaskPriority
import com.speedsoftware.nibbler.ui.theme.NibblerColors

/** A small rounded chip: soft tinted background, coloured text, optional dot. */
@Composable
private fun Chip(label: String, color: Color, modifier: Modifier = Modifier, showLabel: Boolean = true, dot: Boolean = true, fontSize: Int = 11) {
    Row(
        modifier = modifier
            .background(color.copy(alpha = 0.14f), RoundedCornerShape(6.dp))
            .padding(horizontal = 8.dp, vertical = 3.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (dot) Box(modifier = Modifier.size(6.dp).background(color, RoundedCornerShape(3.dp)))
        if (showLabel) {
            if (dot) Spacer(modifier = Modifier.width(6.dp))
            Text(label, fontSize = fontSize.sp, fontWeight = FontWeight.Medium, color = color)
        }
    }
}

private fun typeColor(type: IssueType): Color = when (type) {
    IssueType.BUG -> NibblerColors.Danger
    IssueType.FEATURE -> NibblerColors.Info
    IssueType.TASK -> NibblerColors.OnSurfaceVariant
    IssueType.IMPROVEMENT -> NibblerColors.Warning
    IssueType.STORY -> NibblerColors.Accent
}

@Composable
fun IssueTypeBadge(type: IssueType, modifier: Modifier = Modifier, compact: Boolean = false) {
    Chip(type.displayName, typeColor(type), modifier, showLabel = !compact)
}

fun priorityColor(priority: TaskPriority): Color = when (priority) {
    TaskPriority.URGENT -> NibblerColors.Danger
    TaskPriority.HIGH -> NibblerColors.Warning
    TaskPriority.MEDIUM -> NibblerColors.Info
    TaskPriority.LOW -> NibblerColors.OnSurfaceVariant
}

@Composable
fun PriorityBadge(priority: TaskPriority, modifier: Modifier = Modifier, compact: Boolean = false) {
    val label = when (priority) {
        TaskPriority.URGENT -> "Urgent"
        TaskPriority.HIGH -> "High"
        TaskPriority.MEDIUM -> "Medium"
        TaskPriority.LOW -> "Low"
    }
    Chip(label, priorityColor(priority), modifier, showLabel = !compact)
}

/** The priority as a single line icon (alert / flag / dash / arrow), as used in the corner of board cards. */
@Composable
fun PriorityIcon(priority: TaskPriority, size: Dp = 16.dp, modifier: Modifier = Modifier) {
    val icon = when (priority) {
        TaskPriority.URGENT -> PropertyIcon.ALERT
        TaskPriority.HIGH -> PropertyIcon.PRIORITY
        TaskPriority.MEDIUM -> PropertyIcon.MINUS
        TaskPriority.LOW -> PropertyIcon.ARROW_DOWN
    }
    PropertyIconView(icon, modifier, size, priorityColor(priority))
}

@Composable
fun VersionTag(version: String, modifier: Modifier = Modifier) {
    Chip(if (version.startsWith("v", ignoreCase = true)) version else "v$version", NibblerColors.OnSurfaceVariant, modifier, dot = false)
}

@Composable
fun SprintStatusBadge(status: SprintStatus, modifier: Modifier = Modifier) {
    val (label, color) = when (status) {
        SprintStatus.ACTIVE -> "Active" to NibblerColors.Success
        SprintStatus.PLANNED -> "Planned" to NibblerColors.Info
        SprintStatus.COMPLETED -> "Completed" to NibblerColors.OnSurfaceVariant
    }
    Chip(label, color, modifier, dot = false, fontSize = 10)
}

/** Free-form label chip (for example "Backend" or "Design"), coloured by a few known names. */
@Composable
fun BauhausCategoryChip(name: String, modifier: Modifier = Modifier) {
    val (bg, fg) = when (name.uppercase()) {
        "BACKEND" -> Pair(NibblerColors.TagBackendBg, NibblerColors.TagBackendFg)
        "SECURITY" -> Pair(NibblerColors.TagSecurityBg, NibblerColors.TagSecurityFg)
        "DESIGN" -> Pair(NibblerColors.TagDesignBg, NibblerColors.TagDesignFg)
        "FRONTEND" -> Pair(NibblerColors.TagFrontendBg, NibblerColors.TagFrontendFg)
        else -> Pair(NibblerColors.TagDevOpsBg, NibblerColors.TagDevOpsFg)
    }
    Box(modifier = modifier.background(bg, RoundedCornerShape(6.dp)).padding(horizontal = 8.dp, vertical = 3.dp)) {
        Text(name, fontSize = 11.sp, fontWeight = FontWeight.Medium, color = fg)
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
fun BadgesPreview() {
    Column(modifier = Modifier.background(NibblerColors.Background).padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) { IssueType.values().forEach { IssueTypeBadge(type = it) } }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) { TaskPriority.values().forEach { PriorityBadge(priority = it) } }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) { TaskPriority.values().forEach { PriorityIcon(priority = it) } }
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            VersionTag(version = "1.0.0")
            SprintStatusBadge(status = SprintStatus.ACTIVE)
            BauhausCategoryChip(name = "Backend")
        }
    }
}
